// Iter85 — Freight Deal full-page detail view (replaces side drawer).
import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileText, Edit3, Upload, Download, Trash2, Paperclip, Lock, Send } from "lucide-react";
import {
  useLogisticsRequest, useTransitionLogistics, exportLogisticsPdf,
  STAGE_LABELS, STAGE_COLORS,
  useApprovalState, useRequestApproval, useDecideApproval,
  uploadAttachment, deleteAttachment, attachmentDownloadUrl,
  REJECT_REASON_CATEGORIES, REJECT_REASON_LABELS_I18N,
} from "../api/logistics";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { deriveEditScope } from "./logistics/editor/roleScope";
import { CarrierBidsPanel } from "../components/CarrierBidsPanel";
import { CustomerClaimsPanel } from "../components/CustomerClaimsPanel";
import { SendOutDialog, SendOutHeaderChip, SendOutsPanel } from "../components/SendOutDialog";
import LinkedTripsPanel from "../components/LinkedTripsPanel";
import ContainerIntermodalPanel from "../components/ContainerIntermodalPanel";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// High-contrast palette for workflow CTAs.
const txButtonBg = (stage) => {
  if (stage === "lost" || stage === "cancelled") return "#b22a29";   // red — terminal negative
  if (stage === "won") return "#22a35a";                              // green — terminal positive
  if (stage === "executed") return "#0d6b39";                         // dark-green — Megvalósult
  if (stage === "offered") return "#22a35a";                          // green — Sent / offered
  if (stage === "to_send") return "#7e3aa8";                          // purple — ready-to-send
  if (stage === "waiting_for_approval") return "#b7770d";             // amber — gated
  if (stage === "priced") return "#22a35a";                           // green — ready to ship
  return "#185FA5";                                                   // blue — forward / back-step
};
const txButtonBorder = (stage) => {
  if (stage === "lost" || stage === "cancelled") return "#7a1c1b";
  if (stage === "won" || stage === "offered" || stage === "priced") return "#0f6a3d";
  if (stage === "executed") return "#0a4d29";
  if (stage === "to_send") return "#4f1e6a";
  if (stage === "waiting_for_approval") return "#7a4b09";
  return "#0d3f78";
};

// γ-3-H — Friendly labels for ambiguous transitions. The new chain renames
// the old "Send" action: clicking from `to_send` → `offered` is the moment
// the offer goes out to the customer (stamps offer_sent_at).
const txButtonLabel = (currentStage, targetStage) => {
  if (currentStage === "to_send" && targetStage === "offered") return "Send to customer";
  if (currentStage === "priced" && targetStage === "waiting_for_approval") return "Request approval";
  if (currentStage === "priced" && targetStage === "to_send") return "Mark ready to send";
  if (currentStage === "waiting_for_approval" && targetStage === "to_send") return "Mark ready (approved)";
  if (currentStage === "won" && targetStage === "executed") return "Mark as Megvalósult / Executed";
  return null;  // fall back to STAGE_LABELS[targetStage]
};

const ALLOWED_TRANSITIONS = {
  registered:           ["assigned", "cancelled", "rejected_by_provider"],
  assigned:             ["waiting_for_pricing", "cancelled", "registered", "rejected_by_provider"],
  waiting_for_pricing:  ["priced", "cancelled", "assigned", "rejected_by_provider"],
  priced:               ["waiting_for_approval", "to_send", "cancelled", "waiting_for_pricing", "rejected_by_provider"],
  waiting_for_approval: ["to_send", "cancelled", "priced", "rejected_by_provider"],
  to_send:              ["offered", "lost", "cancelled", "priced", "rejected_by_provider"],
  offered:              ["won", "lost", "cancelled", "to_send", "rejected_by_provider"],
  won:                  ["executed"],
  executed:             [],
  lost:                 ["registered"],
  cancelled:            ["registered"],
  rejected_by_provider: ["registered"],
};

const fmt = (v) => {
  if (v == null || v === "") return "—";
  // γ-3-C — pass React elements through unchanged so KvPanel rows can carry
  // <Link/> + badges without being stringified to '[object Object]'.
  if (React.isValidElement(v)) return v;
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
};
const fmtDate = (s) => s ? String(s).slice(0, 16).replace("T", " ") : "—";

export default function LogisticsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const q = useLogisticsRequest(id);
  const tx = useTransitionLogistics();
  const { locale } = useT();
  const { user: me } = useAuth();
  const approvalQ = useApprovalState(id);
  const requestApproval = useRequestApproval();
  const decide = useDecideApproval();
  const [extra, setExtra] = useState({ closed_reason: "", amount: "", elfogadott_dij: "" });
  const [err, setErr] = useState("");
  // J.5 — Reject-by-provider modal (mandatory reason).
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectDraft, setRejectDraft] = useState({ category: "", text: "" });
  // Resolve assignment user_ids → display label (name · role) so the
  // detail page surfaces colleague assignments human-readably instead of
  // showing raw IDs from the popover-saved felelos/arazo/etc. fields.
  const [colleagues, setColleagues] = useState({});
  // Phase F.3 — Send-out dialog visibility.
  const [showSendOut, setShowSendOut] = useState(false);
  React.useEffect(() => {
    let alive = true;
    axios.get(`${API}/logistics-requests/colleagues/list`)
      .then((r) => {
        if (!alive) return;
        const map = {};
        (r.data.items || []).forEach((u) => { map[u.id] = `${u.name || u.email}${u.role ? ` · ${u.role}` : ""}`; });
        setColleagues(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const colleagueLabel = (uid) => uid ? (colleagues[uid] || uid) : null;

  if (q.isLoading) return <FullPage>Loading…</FullPage>;
  if (!q.data) return <FullPage>Not found.</FullPage>;
  const r = q.data;
  const allowed = ALLOWED_TRANSITIONS[r.deal_stage] || [];
  // γ-3-G — Role-based scope drives the Edit button label + (in-editor)
  // field gating. Pricer sees "Árazás", handler sees "Szerkesztés".
  const editScope = deriveEditScope(me, r);

  const doTx = (stage) => {
    setErr("");
    // J.5 — `rejected_by_provider` requires a mandatory reason; open the
    // modal instead of firing immediately.
    if (stage === "rejected_by_provider") {
      setRejectDraft({ category: "", text: "" });
      setRejectOpen(true);
      return;
    }
    const body = { stage };
    if (stage === "priced" && !r.elfogadott_dij && extra.elfogadott_dij) body.elfogadott_dij = Number(extra.elfogadott_dij);
    if (stage === "won" || stage === "lost") {
      body.closed_reason = extra.closed_reason || (stage === "won" ? "Customer accepted" : "Customer declined");
      if (stage === "won") body.amount = Number(extra.amount) || r.amount || r.celar || 0;
    }
    tx.mutate({ id: r.id, body }, {
      onError: (e) => {
        const d = e?.response?.data?.detail;
        if (typeof d === "object" && d?.code === "approval_required") {
          setErr(`Approval required (triggers: ${(d.triggers || []).join(", ")}). Click "Request approval".`);
          approvalQ.refetch();
          return;
        }
        setErr(typeof d === "object" && d?.missing ? `Missing: ${d.missing.join(", ")}` : (d || "Transition failed"));
      },
    });
  };

  const submitReject = () => {
    if (!rejectDraft.category) { setErr("Pick a rejection reason category"); return; }
    if (!rejectDraft.text || rejectDraft.text.trim().length < 3) { setErr("Reason text must be ≥ 3 characters"); return; }
    tx.mutate(
      { id: r.id, body: { stage: "rejected_by_provider", rejection_reason_category: rejectDraft.category, rejection_reason_text: rejectDraft.text.trim() } },
      {
        onSuccess: () => { setRejectOpen(false); setRejectDraft({ category: "", text: "" }); },
        onError: (e) => {
          const d = e?.response?.data?.detail;
          setErr(typeof d === "object" ? (d.message || JSON.stringify(d)) : (d || "Reject failed"));
        },
      },
    );
  };

  const onRequestApproval = () => {
    setErr("");
    requestApproval.mutate(r.id, {
      onSuccess: () => approvalQ.refetch(),
      onError: (e) => setErr(e?.response?.data?.detail || "Request failed"),
    });
  };
  const onDecide = (decision) => {
    decide.mutate({ rid: r.id, decision }, {
      onSuccess: () => approvalQ.refetch(),
      onError: (e) => setErr(e?.response?.data?.detail || `${decision} failed`),
    });
  };

  return (
    <div className="px-6 py-6" data-testid="logistics-detail-page" style={{ background: "var(--bg, #f6f7fb)", color: "var(--text, #0d1220)", minHeight: "100vh" }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate("/logistics-requests")} className="inline-flex items-center gap-1 px-2 py-1 rounded" style={btnGhost} data-testid="logistics-detail-back">
            <ArrowLeft size={13} /> Back
          </button>
          <div>
            <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 22 }}>{r.deal_name}</h1>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#6b7388", marginTop: 2 }}>
              {r.id} · {r.request_type} · <Badge stage={r.deal_stage} /> · <CompletenessChip r={r} />
              {/* G.1 — Incoterm chip. Commercial term; visible to both
                  forwarder and customer (the customer-tier serializer
                  keeps incoterm + incoterm_named_place). */}
              {r.incoterm && (
                <span
                  className="ml-2 inline-flex items-center px-2 py-0.5 rounded"
                  style={{
                    background: "rgba(126,58,168,0.10)",
                    border: "1px solid rgba(126,58,168,0.30)",
                    color: "#7e3aa8",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.6px",
                  }}
                  data-testid="logistics-detail-incoterm-chip"
                  title={r.incoterm_named_place || "Incoterms 2020"}
                >
                  {r.incoterm}
                  {r.incoterm_named_place && <span style={{ marginLeft: 4, color: "#6b7388", fontWeight: 400 }}>· {r.incoterm_named_place}</span>}
                </span>
              )}
              {/* G.3a — Mode chips + container flag. */}
              {r.is_container_movement && (
                <span
                  className="ml-2 inline-flex items-center px-2 py-0.5 rounded"
                  style={{
                    background: "rgba(10,135,84,0.10)",
                    border: "1px solid rgba(10,135,84,0.35)",
                    color: "#0a8754",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.6px",
                  }}
                  data-testid="logistics-detail-container-chip"
                  title="Container movement"
                >
                  CONTAINER
                </span>
              )}
              {(r.transport_modes || []).map((m) => (
                <span
                  key={m}
                  className="ml-1 inline-flex items-center px-2 py-0.5 rounded"
                  style={{
                    background: "rgba(23,105,170,0.10)",
                    border: "1px solid rgba(23,105,170,0.30)",
                    color: "#1769aa",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                  }}
                  data-testid={`logistics-detail-mode-${m}`}
                >
                  {m}
                </span>
              ))}
              {/* G.3b — POL/POD chips in header (compact) */}
              {r.port_of_loading && (r.port_of_loading.un_locode || r.port_of_loading.name) && (
                <span
                  className="ml-1 inline-flex items-center px-2 py-0.5 rounded"
                  style={{
                    background: "rgba(10,127,196,0.10)",
                    border: "1px solid rgba(10,127,196,0.30)",
                    color: "#0a7fc4",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.6px",
                  }}
                  data-testid="logistics-detail-pol-chip"
                  title="Port of loading"
                >
                  POL · {r.port_of_loading.un_locode || r.port_of_loading.name}
                </span>
              )}
              {r.port_of_discharge && (r.port_of_discharge.un_locode || r.port_of_discharge.name) && (
                <span
                  className="ml-1 inline-flex items-center px-2 py-0.5 rounded"
                  style={{
                    background: "rgba(10,127,196,0.10)",
                    border: "1px solid rgba(10,127,196,0.30)",
                    color: "#0a7fc4",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.6px",
                  }}
                  data-testid="logistics-detail-pod-chip"
                  title="Port of discharge"
                >
                  POD · {r.port_of_discharge.un_locode || r.port_of_discharge.name}
                </span>
              )}
              {/* G.4 — recurring lineage chip (linked to the series template). */}
              {r.created_via_recurring && (
                <Link
                  to={`/admin/recurring-series`}
                  className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded"
                  style={{
                    background: "rgba(180,83,9,0.10)",
                    border: "1px solid rgba(180,83,9,0.35)",
                    color: "#b45309",
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.6px",
                    textDecoration: "none",
                  }}
                  data-testid="logistics-detail-recurring-chip"
                  title={r.recurring_template_name ? `Recurring series: ${r.recurring_template_name}` : "Generated by recurring scheduler"}
                >
                  RECURRING · #{r.recurring_run_index || "?"}
                </Link>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center justify-end" style={{ rowGap: 8 }}>
          {/* γ-3-F (iter95d) — INTERNAL PDF format-selector pill.
              γ-3-H rename — both buttons are INTERNAL (they contain
              margin/target/accepted), so labelling one "Client" was
              misleading — somebody could accidentally mail it. The actual
              customer-facing filtered offer PDF ships with the Customer
              Portal. Renamed to "Internal (Blue)" + "Internal (Executive)"
              so the distinction is purely visual / layout, never audience.
          */}
          <div className="inline-flex items-stretch rounded-md overflow-hidden flex-wrap" style={{ border: "1px solid rgba(0,0,0,0.20)", background: "var(--bg-surface, #ffffff)" }} data-testid="logistics-detail-pdf-pill">
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex",
                alignItems: "center",
                fontFamily: "'DM Mono', monospace",
                fontSize: 9,
                color: "var(--text-dimmer, #6b7388)",
                padding: "0 10px",
                letterSpacing: "0.6px",
                textTransform: "uppercase",
                background: "rgba(0,0,0,0.04)",
                borderRight: "1px solid rgba(0,0,0,0.10)",
                userSelect: "none",
                cursor: "default",
                pointerEvents: "none",
              }}
              data-testid="logistics-detail-pdf-label"
            >
              Internal PDF
            </span>
            <button
              type="button"
              onClick={() => exportLogisticsPdf(r.id, locale, "client")}
              className="inline-flex items-center gap-1 px-3 py-1.5"
              style={{ background: "#185FA5", color: "#ffffff", border: "none", borderRight: "1px solid rgba(0,0,0,0.20)", fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: "0.4px", cursor: "pointer" }}
              data-testid="logistics-detail-pdf-client"
              title="Internal PDF · Command-Blue layout (contains margin/target/accepted — never send to customer)"
            >
              <FileText size={11} /> Internal (Blue)
            </button>
            <button
              type="button"
              onClick={() => exportLogisticsPdf(r.id, locale, "executive")}
              className="inline-flex items-center gap-1 px-3 py-1.5"
              style={{ background: "#0d1220", color: "#ffffff", border: "none", fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: "0.4px", cursor: "pointer" }}
              data-testid="logistics-detail-pdf-executive"
              title="Internal PDF · monochrome executive layout (contains margin/target/accepted — never send to customer)"
            >
              <FileText size={11} /> Internal (Executive)
            </button>
          </div>
          {/* Phase D — Customer-facing PDF (offer / billing). Server applies
              `apply_view_tier_filter(deal, 'customer')` to the rendered doc,
              so this PDF never contains margin/cost regardless of caller. */}
          <button
            type="button"
            onClick={async () => {
              try {
                const variant = r.deal_stage === "executed" ? "billing" : "offer";
                const resp = await axios.get(`${API}/logistics-requests/${r.id}/customer-pdf`, { params: { lang: locale, variant }, responseType: "blob" });
                const blob = new Blob([resp.data], { type: "application/pdf" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `SITROOM_${variant}_${r.id}.pdf`;
                a.click();
                URL.revokeObjectURL(a.href);
              } catch (_) { /* swallow */ }
            }}
            className="inline-flex items-center gap-2 px-3 py-2 rounded"
            style={{
              background: "#7e3aa8",
              color: "#fff",
              border: "none",
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4px",
              cursor: "pointer",
            }}
            data-testid="logistics-detail-customer-pdf"
            title="Customer-facing PDF — auto-picks Offer (pre-exec) or Billing (post-exec). Never contains margin/cost."
          >
            <Send size={12} /> {r.deal_stage === "executed" ? "Customer Billing PDF" : "Customer Offer PDF"}
          </button>
          {/* Phase E — Share offer PDF via no-auth link */}
          <button
            type="button"
            onClick={async () => {
              const label = window.prompt("Recipient label (e.g. company name):");
              if (!label) return;
              try {
                const resp = await axios.post(`${API}/carrier-links/offer-share`, {
                  logistics_request_id: r.id,
                  recipient_label: label,
                  lang: locale || "en",
                });
                const url = resp.data.public_url;
                try {
                  await navigator.clipboard.writeText(url);
                  window.alert(`Link copied to clipboard:\n${url}`);
                } catch (_) {
                  window.prompt("Copy this link:", url);
                }
              } catch (_) { /* swallow */ }
            }}
            className="inline-flex items-center gap-2 px-3 py-2 rounded"
            style={{
              background: "transparent",
              color: "#7e3aa8",
              border: "1px solid #7e3aa8",
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.4px",
              cursor: "pointer",
            }}
            data-testid="logistics-detail-offer-share"
            title="Generate a no-auth public link → share via email/WhatsApp"
          >
            <Send size={12} /> Share offer link
          </button>
          {/* Phase F.3 — Send-out dialog (records audit, routes via resolver) */}
          <button
            type="button"
            onClick={() => setShowSendOut(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded"
            style={{ background: "#0a8754", color: "#fff", border: "none", fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.4px", cursor: "pointer" }}
            data-testid="logistics-detail-send-out"
            title="Send-out dialog · auto-routes via customer-master + records audit. Brevo MOCKED."
          >
            <Send size={12} /> Send PDF
          </button>
          {/* "Last sent to" inline chip — keep audit visible at the
              point-of-action so the forwarder doesn't double-send. */}
          <SendOutHeaderChip rid={r.id} onOpenTrail={() => setShowSendOut(false)} />
          <Link
            to={`/logistics-requests/${r.id}/edit`}
            className="inline-flex items-center gap-2 px-3 py-2 rounded"
            style={btnPrimary}
            data-testid="logistics-detail-edit"
            title={editScope.scope === "pricer" ? "Pricing edit (Árazás) — only pricing fields are writable" : editScope.scope === "handler" ? "Handler edit (Szerkesztés) — route/cargo/stops are writable; pricing read-only" : "Edit deal"}
          >
            <Edit3 size={13} /> {editScope.buttonLabel || "Edit"}
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        <div className="space-y-3">
          <KvPanel title="Identification" rows={[
            ["Azonosító", r.azonosito],
            ["Ajánlat jellege", r.ajanlat_jellege],
            ["Ajánlati szint", r.ajanlati_szint],
            ["Forgalom iránya", r.forgalom_iranya],
            ["Tender", r.is_tender ? "Yes" : null],
            ["Ajánlat érvényessége", fmtDate(r.ajanlat_ervenyessege)],
          ]} />
          <KvPanel title="Customer" rows={[["Name", r.customer_name], ["Category", r.customer_category_id], ["Email", r.customer_email], ["Phone", r.customer_phone]]} />
          <KvPanel title="Cargo" rows={[
            ["Áru", r.aru_megnevezese], ["Autótípus", r.autotipus],
            ["Súly (kg)", r.suly_kg], ["LDM", r.rakmeter], ["Köbméter", r.kobmeter],
            ["ADR", r.adr ? "Yes" : (r.adr === false ? "No" : "—")], ["UN szám", r.un_szam],
            ["Rakatolás módja", r.rakatolas_modja], ["Hűtés (°C)", r.hutes_hofok],
            ["Kiegészítő szolgáltatások", r.kiegeszito_szolgaltatasok],
          ]} />
          <Panel title={`Pickups (${(r.pickups || []).length})`}>
            {r.priority_side && (
              <div className="mb-2" style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#185FA5", padding: "2px 6px", background: "rgba(24,95,165,0.08)", borderRadius: 3, display: "inline-block" }} data-testid="logistics-detail-priority-side">
                Priority side: <strong>{r.priority_side}</strong>
              </div>
            )}
            {(r.pickups || []).map((s, i) => (
              <StopRowDetail key={i} s={s} i={i} prefix="pickup" />
            ))}
          </Panel>
          <Panel title={`Dropoffs (${(r.dropoffs || []).length})`}>
            {(r.dropoffs || []).map((s, i) => (
              <StopRowDetail key={i} s={s} i={i} prefix="dropoff" />
            ))}
          </Panel>
          {/* iter95 — Assignment moved from right sidebar into the main column
              below Pickup/Dropoff so side-panel-entered colleague fields are
              immediately visible to the user (was hidden in the workflow
              sidebar previously). */}
          <Panel title="Assignment">
            <KvRow k="Felelős" v={colleagueLabel(r.felelos_user_id)} />
            <KvRow k="Tulajdonos" v={colleagueLabel(r.deal_tulajdonos_user_id)} />
            <KvRow k="Ügyfélszolgálat" v={colleagueLabel(r.ugyfelszolgalat_user_id)} />
            <KvRow k="Árazó" v={colleagueLabel(r.arazo_user_id)} />
            <KvRow k="Külső szállítmányozó" v={r.external_forwarder_name || r.external_forwarder_tenant_id} />
          </Panel>
          <KvPanel title="Pricing" rows={[
            ["Currency", r.currency], ["Amount", r.amount], ["Célár", r.celar],
            ["Ajánlati margin %", r.ajanlati_margin_pct], ["Elfogadott díj", r.elfogadott_dij],
            ["Fuvarozás díj/költség/margin", `${fmt(r.fuvarozas_dij)} / ${fmt(r.fuvarozas_koltseg)} / ${fmt(r.fuvarozas_margin)}`],
            ["Szállítmányozás díj/költség/margin", `${fmt(r.szallitmanyozas_dij)} / ${fmt(r.szallitmanyozas_koltseg)} / ${fmt(r.szallitmanyozas_margin)}`],
            ["Added value díj/költség/margin", `${fmt(r.added_value_dij)} / ${fmt(r.added_value_koltseg)} / ${fmt(r.added_value_margin)}`],
            ["Konkurencia díj", r.konkurencia_dij],
            ["Surcharges total", r.surcharges_total],
          ]} />
          {(r.surcharges || []).length > 0 && <SurchargesDetail surcharges={r.surcharges} currency={r.currency} />}
          {/* γ-3-G — Megvalósult (Executed) post-execution panel. Renders only
              once the deal has been marked as executed. Highlights the STRICT
              separation between Delay (carrier fault, KPI) and Waiting
              (site fault, surcharge — listed in the Surcharges panel above). */}
          {(r.deal_stage === "executed" || r.executed_at || r.delay_minutes != null || r.delay_notes || r.executed_notes) && (
            <Panel title="Megvalósult / Executed — post-execution debrief">
              <div data-testid="logistics-detail-executed-panel">
                <div className="mb-2 px-2 py-1.5 rounded" style={{ background: "rgba(13,107,57,0.08)", border: "1px solid rgba(13,107,57,0.40)", fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#0d6b39" }}>
                  Executed at: <strong>{fmtDate(r.executed_at)}</strong>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div className="rounded p-2" style={{ background: "rgba(178,42,41,0.06)", border: "1px solid rgba(178,42,41,0.35)" }} data-testid="logistics-detail-delay">
                    <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 10, color: "#b22a29", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 4 }}>
                      Delay · Carrier fault · KPI
                    </div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 18, color: "#b22a29", fontWeight: 700 }} data-testid="logistics-detail-delay-minutes">
                      {r.delay_minutes != null ? `${r.delay_minutes} min` : "—"}
                    </div>
                    {r.delay_notes && (
                      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text, #0d1220)", marginTop: 6, whiteSpace: "pre-wrap" }}>
                        {r.delay_notes}
                      </div>
                    )}
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 8, color: "#6b7388", marginTop: 4, fontStyle: "italic" }}>
                      Feeds the carrier-delay KPI. Set ONLY in Megvalósult stage.
                    </div>
                  </div>
                  <div className="rounded p-2" style={{ background: "rgba(24,95,165,0.06)", border: "1px solid rgba(24,95,165,0.35)" }} data-testid="logistics-detail-waiting-pointer">
                    <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 10, color: "#185FA5", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 4 }}>
                      Waiting · Site fault · Surcharge
                    </div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text, #0d1220)" }}>
                      Recorded as a &ldquo;Várakozás / Waiting time&rdquo; surcharge line above. Never merged with Delay.
                    </div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 8, color: "#6b7388", marginTop: 4, fontStyle: "italic" }}>
                      Add via the Surcharges editor (hidden category).
                    </div>
                  </div>
                </div>
                {r.executed_notes && (
                  <div className="mt-2 rounded p-2" style={{ background: "rgba(13,107,57,0.04)", border: "1px solid rgba(13,107,57,0.25)" }}>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 2 }}>Execution debrief</div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text, #0d1220)", whiteSpace: "pre-wrap" }} data-testid="logistics-detail-executed-notes">
                      {r.executed_notes}
                    </div>
                  </div>
                )}
                {/* G.1 — "Who & What executes this freight" — own/subcontractor
                    + plate + driver. Editable in LogisticsEditor → Execution
                    group; displayed here once any field is set. */}
                {(r.vehicle_provider || r.subcontractor_name || r.vehicle_plate || r.trailer_plate || r.driver_name) && (
                  <div className="mt-2 rounded p-2" style={{ background: "rgba(126,58,168,0.05)", border: "1px solid rgba(126,58,168,0.30)" }} data-testid="logistics-detail-execution-group">
                    <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 10, color: "#7e3aa8", letterSpacing: "0.6px", textTransform: "uppercase", marginBottom: 4 }}>
                      Who &amp; what executes this freight
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {r.vehicle_provider && (
                        <div data-testid="exec-provider">
                          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388", textTransform: "uppercase", letterSpacing: "0.5px" }}>Provider</div>
                          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 700, color: r.vehicle_provider === "own" ? "#0a8754" : "#7e3aa8" }}>{r.vehicle_provider === "own" ? "Own fleet" : "Subcontractor"}</div>
                        </div>
                      )}
                      {r.subcontractor_name && (
                        <div data-testid="exec-subcontractor">
                          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388", textTransform: "uppercase", letterSpacing: "0.5px" }}>Subcontractor</div>
                          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{r.subcontractor_name}</div>
                        </div>
                      )}
                      {r.vehicle_plate && (
                        <div data-testid="exec-vehicle-plate">
                          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388", textTransform: "uppercase", letterSpacing: "0.5px" }}>Vehicle plate</div>
                          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 700, letterSpacing: "0.5px" }}>{r.vehicle_plate}</div>
                        </div>
                      )}
                      {r.trailer_plate && (
                        <div data-testid="exec-trailer-plate">
                          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388", textTransform: "uppercase", letterSpacing: "0.5px" }}>Trailer plate</div>
                          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 700, letterSpacing: "0.5px" }}>{r.trailer_plate}</div>
                        </div>
                      )}
                      {r.driver_name && (
                        <div data-testid="exec-driver-name">
                          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388", textTransform: "uppercase", letterSpacing: "0.5px" }}>Driver</div>
                          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{r.driver_name}</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Panel>
          )}
          {r.vehicle_delay_flag && (
            <div className="rounded-md p-3" style={{ background: "rgba(178,42,41,0.08)", border: "1px solid rgba(178,42,41,0.45)" }} data-testid="logistics-detail-vehicle-delay">
              <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, color: "#b22a29", letterSpacing: "0.5px" }}>VEHICLE DELAY</div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#6b7388", marginTop: 2 }}>This deal counts toward the Vehicle delays KPI.</div>
            </div>
          )}
          <KvPanel title="Dates" rows={[
            ["Árazásra kiküldve", fmtDate(r.arazasra_kikuldve_at)],
            ["Ajánlati (offer-ready)", fmtDate(r.offered_at)],
            ["Kiküldés (Send)", fmtDate(r.offer_sent_at)],
            ["Leadási határidő", fmtDate(r.leadasi_hatarido)],
            ["Kezdési dátum", fmtDate(r.kezdesi_datum)],
            ["Projekt kezdete", fmtDate(r.projekt_kezdete)],
            ["Ajánlat érvényessége", fmtDate(r.ajanlat_ervenyessege)],
          ]} />
          {r.is_tender ||
           r.szerzodes_idotartama_months != null ||
           r.tender_atfutasi_napok != null ||
           r.kovetkezo_tender_date ||
           r.tender_feltoltes_at ||
           r.tender_kikuldes_at ||
           r.tender_visszaigazolas_filename ||
           r.tender_pricing_filename ||
           r.linked_tender_id ||
           r.linked_tender_external_ref ||
           r.linked_tender_note ? (
            <KvPanel title="Tender" rows={[
              ["Tender", r.is_tender ? "Yes" : null],
              ["Szerződés (hó)", r.szerzodes_idotartama_months],
              ["Átfutás (nap)", r.tender_atfutasi_napok],
              ["Következő tender", fmtDate(r.kovetkezo_tender_date)],
              ["Tender feltöltés", fmtDate(r.tender_feltoltes_at)],
              ["Tender kiküldés", fmtDate(r.tender_kikuldes_at)],
              ["Tender visszaigazolás", r.tender_visszaigazolas_filename],
              ["Tender pricing file", r.tender_pricing_filename],
              ["Linked tender ID", r.linked_tender_id ? (
                <Link to={`/logistics-requests/${r.linked_tender_id}`} style={{ color: "#185FA5", textDecoration: "underline" }} data-testid="logistics-detail-linked-tender">
                  {r.linked_tender_id}
                </Link>
              ) : null],
              ["External tender ref", r.linked_tender_external_ref],
              ["Linked tender note", r.linked_tender_note],
            ]} />
          ) : null}
          <AttachmentsPanel rid={r.id} attachments={r.attachments || []} />
          <KvPanel title="Documents (legacy text-only)" rows={[
            ["Bejövő dokumentumok", r.bejovo_dokumentumok],
            ["Kiküldött dokumentumok", r.kikuldott_dokumentumok],
          ]} />
          <KvPanel title="Notes" rows={[
            ["Kiegészítő info", r.kiegeszito_info],
            ["Szakmai kifogás", r.szakmai_kifogas],
            ["Closed reason", r.closed_reason],
          ]} />
          {/* iter95 — Safety net: catch any populated side-panel parameter not
              explicitly rendered in one of the panels above. Prevents future
              drift between PARAMS in the editor and explicitly-rendered fields
              on the detail view. */}
          <AdditionalParametersPanel r={r} />
        </div>

        {/* RIGHT — workflow controls */}
        <div className="space-y-3">
          <ApprovalBanner state={approvalQ.data} me={me} onRequest={onRequestApproval} onApprove={() => onDecide("approve")} onReject={() => onDecide("reject")} />
          <Panel title="Workflow">
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#6b7388", marginBottom: 6 }}>Current: <Badge stage={r.deal_stage} /></div>
            {r.deal_stage === "rejected_by_provider" && (
              <div className="mb-3 p-2 rounded" style={{ background: "rgba(126,58,168,0.10)", border: "1px solid rgba(126,58,168,0.40)" }} data-testid="reject-summary">
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#c89adb", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                  {STAGE_LABELS["rejected_by_provider"]}
                </div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#f0f2f8", marginTop: 2 }} data-testid="reject-category-display">
                  {(REJECT_REASON_LABELS_I18N[locale] || REJECT_REASON_LABELS_I18N.en)[r.rejection_reason_category] || r.rejection_reason_category}
                </div>
                {r.rejection_reason_text && (
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#cbd5e1", marginTop: 4 }} data-testid="reject-text-display">
                    {r.rejection_reason_text}
                  </div>
                )}
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#94a3b8", marginTop: 4 }}>
                  from <strong style={{ color: "#9ec3ec" }}>{r.rejected_by_provider_from}</strong> · {fmtDate(r.rejected_by_provider_at)}
                </div>
              </div>
            )}
            {allowed.length === 0 ? (
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#6b7388" }}>Terminal stage.</div>
            ) : (
              <ClickableTimeline
                currentStage={r.deal_stage}
                allowedNext={allowed}
                onClick={(s) => doTx(s)}
              />
            )}
            {allowed.includes("priced") && !r.elfogadott_dij && (
              <div className="mt-3">
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388" }}>Elfogadott díj (required for `priced`)</div>
                <input type="number" value={extra.elfogadott_dij} onChange={(e) => setExtra({ ...extra, elfogadott_dij: e.target.value })} className="w-full px-2 py-1.5 rounded" style={inputStyle()} data-testid="logistics-tx-elfdij" />
              </div>
            )}
            {(allowed.includes("won") || allowed.includes("lost")) && (
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388" }}>Closed reason</div>
                  <input value={extra.closed_reason} onChange={(e) => setExtra({ ...extra, closed_reason: e.target.value })} className="w-full px-2 py-1.5 rounded" style={inputStyle()} data-testid="logistics-tx-reason" />
                </div>
                <div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388" }}>Amount (for won)</div>
                  <input type="number" value={extra.amount} onChange={(e) => setExtra({ ...extra, amount: e.target.value })} className="w-full px-2 py-1.5 rounded" style={inputStyle()} data-testid="logistics-tx-amount" />
                </div>
              </div>
            )}
            {err && <div className="mt-3 px-2 py-1 rounded" style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.45)", color: "#b22a29", fontFamily: "'DM Mono', monospace", fontSize: 10 }} data-testid="logistics-tx-error">{err}</div>}
          </Panel>
          {/* iter95 — Assignment panel moved into the main (left) column for visibility. */}
        </div>
      </div>

      {/* Phase C — Carrier bidder panel: rates received + generate link */}
      <CarrierBidsPanel logisticsRequestId={r.id} />

      {/* G.3 — Container ports + pre/on-carriage + intermodal connection points. */}
      <ContainerIntermodalPanel deal={r} />

      {/* Phase E — Customer claims triage panel (shows only when claims exist) */}
      <CustomerClaimsPanel logisticsRequestId={r.id} />
      {/* G.2 — Linked Trips panel: bidirectional links to other deals. */}
      <LinkedTripsPanel rid={r.id} />
      {/* Phase F.3 — Send-out audit trail */}
      <SendOutsPanel rid={r.id} />
      {showSendOut && (
        <SendOutDialog
          rid={r.id}
          variant={r.deal_stage === "executed" ? "billing" : "offer"}
          onClose={() => setShowSendOut(false)}
        />
      )}

      {/* J.5 — Reject-by-provider modal */}
      {rejectOpen && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setRejectOpen(false); }}
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          data-testid="reject-by-provider-modal"
        >
          <div
            className="rounded-lg p-5 max-w-md w-full mx-4"
            style={{ background: "#0f1626", border: "1px solid #7e3aa8", boxShadow: "0 10px 40px rgba(0,0,0,0.5)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 17, color: "#fff", letterSpacing: "-0.3px" }}>
              {STAGE_LABELS["rejected_by_provider"]}
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#94a3b8", marginTop: 2, marginBottom: 14 }}>
              From stage <strong style={{ color: "#9ec3ec" }}>{r.deal_stage}</strong>. The deal will be marked terminal. Reason is mandatory and recorded in history.
            </div>

            <label className="block mb-3">
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#94a3b8", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 4 }}>
                Reason category *
              </div>
              <select
                value={rejectDraft.category}
                onChange={(e) => setRejectDraft((d) => ({ ...d, category: e.target.value }))}
                className="w-full px-2 py-1.5 rounded"
                style={{ background: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.16)", color: "#f0f2f8", fontFamily: "'DM Mono', monospace", fontSize: 12 }}
                data-testid="reject-category-select"
              >
                <option value="">— pick a reason —</option>
                {REJECT_REASON_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{(REJECT_REASON_LABELS_I18N[locale] || REJECT_REASON_LABELS_I18N.en)[c]}</option>
                ))}
              </select>
            </label>

            <label className="block mb-4">
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#94a3b8", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 4 }}>
                Details *
              </div>
              <textarea
                value={rejectDraft.text}
                onChange={(e) => setRejectDraft((d) => ({ ...d, text: e.target.value }))}
                rows={3}
                placeholder="What happened? min 3 characters"
                className="w-full px-2 py-1.5 rounded"
                style={{ background: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.16)", color: "#f0f2f8", fontFamily: "'DM Mono', monospace", fontSize: 12 }}
                data-testid="reject-text-input"
              />
            </label>

            {err && (
              <div className="mb-3 px-2 py-1.5 rounded" style={{ background: "rgba(226,75,74,0.12)", border: "1px solid rgba(226,75,74,0.40)", color: "#ff8d8c", fontFamily: "'DM Mono', monospace", fontSize: 10 }} data-testid="reject-error">
                {err}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setRejectOpen(false); setErr(""); }}
                className="px-3 py-1.5 rounded"
                style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#cbd5e1", fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
                data-testid="reject-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitReject}
                disabled={tx.isPending}
                className="px-3 py-1.5 rounded"
                style={{ background: "#7e3aa8", border: "none", color: "#fff", fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", cursor: tx.isPending ? "wait" : "pointer", opacity: tx.isPending ? 0.7 : 1 }}
                data-testid="reject-confirm"
              >
                {tx.isPending ? "Marking…" : "Mark rejected"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const FullPage = ({ children }) => <div className="px-6 py-6" style={{ background: "var(--bg, #f6f7fb)", color: "var(--text, #0d1220)", minHeight: "100vh", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{children}</div>;

const Panel = ({ title, children }) => (
  <div className="rounded p-3" style={{ background: "var(--bg-surface, #ffffff)", border: "1px solid var(--border-strong, rgba(0,0,0,0.10))" }}>
    <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{title}</div>
    {children}
  </div>
);

const KvPanel = ({ title, rows }) => {
  const filtered = rows.filter(([, v]) => v != null && v !== "" && !(Array.isArray(v) && v.length === 0));
  if (filtered.length === 0) return null;
  return (
    <Panel title={title}>
      <div className="space-y-1">
        {filtered.map(([k, v]) => <KvRow key={k} k={k} v={v} />)}
      </div>
    </Panel>
  );
};

const KvRow = ({ k, v }) => v == null || v === "" ? null : (
  <div className="flex items-baseline justify-between gap-3">
    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#6b7388" }}>{k}</span>
    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{fmt(v)}</span>
  </div>
);

// ──────────────── J.5 · Clickable vertical timeline ─────────────────────
// Replaces the right-side stage button strip with an interactive
// vertical timeline mirroring the customer-portal visual.
const WORKFLOW_STAGE_CHAIN = [
  "registered", "assigned", "waiting_for_pricing", "priced",
  "waiting_for_approval", "to_send", "offered", "won", "executed",
];
const SIDE_OUTCOMES = ["cancelled", "lost", "rejected_by_provider"];
const tlMono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace", fontSize: size, color: color || "#f0f2f8",
  fontWeight: weight || 400, letterSpacing: ls,
});

const ClickableTimeline = ({ currentStage, allowedNext, onClick }) => {
  const allowed = new Set(allowedNext || []);
  const curIdx = WORKFLOW_STAGE_CHAIN.indexOf(currentStage);
  return (
    <div data-testid="lr-clickable-timeline">
      <div className="relative" style={{ paddingLeft: 30 }}>
        <div
          aria-hidden
          style={{ position: "absolute", left: 9, top: 6, bottom: 6, width: 2,
                   background: "linear-gradient(180deg, rgba(24,95,165,0.50) 0%, rgba(24,95,165,0.10) 100%)" }}
        />
        {WORKFLOW_STAGE_CHAIN.map((st, idx) => {
          const completed = idx < curIdx && curIdx !== -1;
          const current = st === currentStage;
          const clickable = allowed.has(st);
          const color = completed ? "#4ed084" : current ? "#9ec3ec" : clickable ? "#0a8754" : "#5a6275";
          return (
            <div
              key={st}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : -1}
              onClick={() => clickable && onClick(st)}
              onKeyDown={(e) => clickable && (e.key === "Enter" || e.key === " ") && onClick(st)}
              className="relative mb-2 last:mb-0"
              data-testid={`lr-timeline-stage-${st}`}
              data-state={completed ? "completed" : current ? "current" : clickable ? "clickable" : "pending"}
              style={{ cursor: clickable ? "pointer" : "default", padding: "6px 8px", borderRadius: 6, transition: "background 120ms ease",
                       background: clickable ? "rgba(10,135,84,0.06)" : "transparent",
                       border: clickable ? "1px solid rgba(10,135,84,0.40)" : "1px solid transparent" }}
              onMouseEnter={(e) => { if (clickable) e.currentTarget.style.background = "rgba(10,135,84,0.18)"; }}
              onMouseLeave={(e) => { if (clickable) e.currentTarget.style.background = "rgba(10,135,84,0.06)"; }}
            >
              <div
                style={{ position: "absolute", left: -30, top: 6, width: 20, height: 20, borderRadius: "50%",
                         background: "#0d0f18", border: `2px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <span style={{ ...tlMono(10, color, 800) }}>
                  {completed ? "✓" : current ? "•" : idx + 1}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span style={{ ...tlMono(12, color, current || completed || clickable ? 700 : 500, "0.4px"), textTransform: "uppercase" }}>
                  {STAGE_LABELS[st]}
                </span>
                {clickable && (
                  <span style={{ ...tlMono(9, "#0a8754", 700, "0.6px"), background: "rgba(10,135,84,0.12)",
                                  padding: "2px 6px", borderRadius: 4, textTransform: "uppercase",
                                  border: "1px solid rgba(10,135,84,0.40)" }}>
                    Click to advance
                  </span>
                )}
                {current && (
                  <span style={{ ...tlMono(9, "#9ec3ec", 700, "0.6px"), background: "rgba(24,95,165,0.18)",
                                  padding: "2px 6px", borderRadius: 4, textTransform: "uppercase" }}>
                    Now
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {(allowed.has("cancelled") || allowed.has("lost") || allowed.has("rejected_by_provider")) && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          {SIDE_OUTCOMES.filter((s) => allowed.has(s)).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onClick(s)}
              className="px-3 py-1.5 rounded"
              style={{
                background: s === "rejected_by_provider" ? "#7e3aa8" : s === "lost" ? "#a85c4a" : "#5a6275",
                border: "none", color: "#ffffff",
                ...tlMono(10, "#ffffff", 700, "0.4px"),
                textTransform: "uppercase", cursor: "pointer",
              }}
              data-testid={`logistics-transition-${s}`}
            >
              → {STAGE_LABELS[s]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


const Badge = ({ stage }) => (
  <span className="inline-block px-1.5 py-0.5 rounded" style={{ background: STAGE_COLORS[stage] || "#9198b0", color: "#ffffff", fontFamily: "'DM Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase" }}>
    {STAGE_LABELS[stage] || stage}
  </span>
);

const inputStyle = () => ({ background: "#ffffff", border: "1px solid rgba(0,0,0,0.20)", color: "#0d1220", fontFamily: "'DM Mono', monospace", fontSize: 11 });
const btnPrimary = { background: "#185FA5", border: "1px solid #0d3f78", color: "#ffffff", fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, letterSpacing: "0.5px", cursor: "pointer", textShadow: "0 1px 0 rgba(0,0,0,0.18)", textDecoration: "none" };
const btnSecondary = { background: "#ffffff", border: "1px solid #185FA5", color: "#185FA5", fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 11, letterSpacing: "0.4px", cursor: "pointer" };
const btnGhost = { background: "transparent", border: "1px solid rgba(0,0,0,0.20)", color: "#0d1220", fontFamily: "'DM Mono', monospace", fontWeight: 600, fontSize: 11, letterSpacing: "0.4px", cursor: "pointer" };


// ───────────────────────── Approval banner (iter86b B5) ─────────────────────────

const ApprovalBanner = ({ state, me, onRequest, onApprove, onReject }) => {
  if (!state) return null;
  const isAdmin = me && (me.role === "platform_admin" || me.role === "tenant_admin");
  const latest = state.latest;
  // No prior approval row — show the "request approval" CTA only if the
  // configured rule actually fires for this deal (otherwise nothing to do).
  if (!latest) {
    if (!state.needs_approval) return null;
    return (
      <div className="rounded-md p-3" style={{ background: "rgba(183,119,13,0.12)", border: "1px solid #b7770d" }} data-testid="logistics-approval-banner-needed">
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, color: "#b7770d", marginBottom: 4, letterSpacing: "0.3px" }}>APPROVAL REQUIRED</div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#6b7388", marginBottom: 6 }}>
          Triggers: {(state.triggers || []).join(", ") || "(rule)"}
        </div>
        <button type="button" onClick={onRequest} className="px-3 py-1.5 rounded" style={{ background: "#b7770d", border: "1px solid #7a5009", color: "#ffffff", fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, cursor: "pointer", letterSpacing: "0.4px" }} data-testid="logistics-approval-request-btn">
          REQUEST APPROVAL
        </button>
      </div>
    );
  }
  if (latest.status === "pending") {
    return (
      <div className="rounded-md p-3" style={{ background: "rgba(24,95,165,0.10)", border: "1px solid #185FA5" }} data-testid="logistics-approval-banner-pending">
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, color: "#185FA5", marginBottom: 4, letterSpacing: "0.3px" }}>APPROVAL PENDING</div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#6b7388", marginBottom: 6 }}>
          Awaiting admin decision · Triggers: {(latest.triggers || []).join(", ") || "—"}
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button type="button" onClick={onApprove} className="px-3 py-1.5 rounded" style={{ background: "#22a35a", border: "1px solid #0d6b39", color: "#ffffff", fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, cursor: "pointer", letterSpacing: "0.4px" }} data-testid="logistics-approval-approve-btn">
              APPROVE
            </button>
            <button type="button" onClick={onReject} className="px-3 py-1.5 rounded" style={{ background: "#b22a29", border: "1px solid #7a1c1b", color: "#ffffff", fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 700, cursor: "pointer", letterSpacing: "0.4px" }} data-testid="logistics-approval-reject-btn">
              REJECT
            </button>
          </div>
        )}
      </div>
    );
  }
  if (latest.status === "approved") {
    return (
      <div className="rounded-md p-3" style={{ background: "rgba(34,163,90,0.10)", border: "1px solid #22a35a" }} data-testid="logistics-approval-banner-approved">
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, color: "#22a35a", marginBottom: 4, letterSpacing: "0.3px" }}>APPROVED</div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#6b7388" }}>
          Decided at {latest.decided_at?.slice(0, 16) || "—"}
        </div>
      </div>
    );
  }
  if (latest.status === "rejected") {
    return (
      <div className="rounded-md p-3" style={{ background: "rgba(178,42,41,0.10)", border: "1px solid #b22a29" }} data-testid="logistics-approval-banner-rejected">
        <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, color: "#b22a29", marginBottom: 4, letterSpacing: "0.3px" }}>REJECTED</div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#6b7388", marginBottom: 6 }}>
          {latest.note || "Approval declined by admin"}
        </div>
        <button type="button" onClick={onRequest} className="px-3 py-1.5 rounded" style={{ background: "transparent", border: "1px solid #b22a29", color: "#b22a29", fontFamily: "'DM Mono', monospace", fontSize: 10, fontWeight: 700, cursor: "pointer" }} data-testid="logistics-approval-request-again-btn">
          REQUEST AGAIN
        </button>
      </div>
    );
  }
  return null;
};


// γ-2 — Per-stop detail row: shows kind, merged window range, priority,
// loading info, per-stop cargo, and göngyöleg items if any. Compact but
// information-dense for the read-only detail view.
const KIND_TINT = {
  pickup:         { label: "Felrakó",          color: "#185FA5" },
  dropoff_pickup: { label: "Lerakó+Felrakó",   color: "#7e3aa8" },
  dropoff:        { label: "Lerakó",           color: "#22a35a" },
};
const PRIO_TINT = {
  fixed: { label: "FIXED",  color: "#b22a29" },
  flex:  { label: "FLEX",   color: "#22a35a" },
  asap:  { label: "ASAP",   color: "#b7770d" },
};
const StopRowDetail = ({ s, i, prefix }) => {
  const kind = s.kind || (prefix === "pickup" ? "pickup" : "dropoff");
  const km = KIND_TINT[kind] || KIND_TINT.pickup;
  const wStart = (s.window_start_at || s.date_time || "").slice(0, 16).replace("T", " ");
  const wEnd = (s.window_end_at || "").slice(0, 16).replace("T", " ");
  const when = wEnd
    ? (wEnd.slice(0, 10) === wStart.slice(0, 10) ? `${wStart} → ${wEnd.slice(11)}` : `${wStart} → ${wEnd}`)
    : (wStart || "—");
  const pr = PRIO_TINT[s.priority];
  const cargoBits = [];
  if (s.aru_megnevezese) cargoBits.push(s.aru_megnevezese);
  if (s.suly_kg != null) cargoBits.push(`${s.suly_kg} kg`);
  if (s.rakmeter != null) cargoBits.push(`${s.rakmeter} LDM`);
  if (s.kobmeter != null) cargoBits.push(`${s.kobmeter} m³`);
  const gongy = s.gongyoleg_items || [];
  return (
    <div className="rounded p-2 mb-2" style={{ background: "var(--bg, #f6f7fb)", border: "1px solid var(--border-strong, rgba(0,0,0,0.10))", borderLeft: `3px solid ${km.color}` }} data-testid={`logistics-detail-${prefix}-${i}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 11, color: km.color, letterSpacing: "0.3px" }} data-testid={`logistics-detail-${prefix}-${i}-kind`}>
          {km.label} #{i + 1}
        </span>
        {pr && (
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#ffffff", background: pr.color, padding: "1px 6px", borderRadius: 3, fontWeight: 700, letterSpacing: "0.5px" }} data-testid={`logistics-detail-${prefix}-${i}-priority`}>
            {pr.label}
          </span>
        )}
      </div>
      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "var(--text, #0d1220)" }} data-testid={`logistics-detail-${prefix}-${i}-when`}>
        {when} · {s.country || ""} {s.postal_code || ""} {s.city || ""} {s.street ? `· ${s.street} ${s.house_number || ""}` : ""}
      </div>
      {(s.entry_gate || s.dock_number || s.time_window || s.customs) && (
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#6b7388", marginTop: 2 }}>
          {s.entry_gate ? `Gate: ${s.entry_gate} · ` : ""}{s.dock_number ? `Dock: ${s.dock_number} · ` : ""}{s.time_window ? `Window: ${s.time_window}` : ""}{s.customs ? " · Vámkezelés" : ""}
        </div>
      )}
      {(s.loading_minutes != null || s.loading_notes) && (
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#6b7388", marginTop: 2 }} data-testid={`logistics-detail-${prefix}-${i}-loading`}>
          {s.loading_minutes != null ? `Loading: ${s.loading_minutes} min` : ""}
          {s.loading_minutes != null && s.loading_notes ? " · " : ""}
          {s.loading_notes || ""}
        </div>
      )}
      {(s.loading_sides || []).length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1" data-testid={`logistics-detail-${prefix}-${i}-loading-sides`}>
          {(s.loading_sides || []).map((side) => (
            <span key={side} style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#185FA5", background: "rgba(24,95,165,0.10)", padding: "1px 5px", borderRadius: 3, fontWeight: 700, letterSpacing: "0.5px" }}>
              {side.toUpperCase().replace("_", " ")}
            </span>
          ))}
        </div>
      )}
      {cargoBits.length > 0 && (
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text, #0d1220)", marginTop: 4, padding: "2px 4px", background: "rgba(24,95,165,0.06)", borderRadius: 3 }} data-testid={`logistics-detail-${prefix}-${i}-cargo`}>
          <span style={{ color: "#6b7388", letterSpacing: "0.5px", textTransform: "uppercase", fontSize: 8, marginRight: 4 }}>Cargo</span>
          {cargoBits.join(" · ")}
        </div>
      )}
      {gongy.length > 0 && (
        <div className="mt-1" data-testid={`logistics-detail-${prefix}-${i}-gongyoleg`}>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 8, color: "#7e3aa8", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 2, fontWeight: 700 }}>Göngyöleg ({gongy.length})</div>
          {gongy.map((g, gi) => {
            const bits = [];
            if (g.aru_megnevezese) bits.push(g.aru_megnevezese);
            if (g.suly_kg != null) bits.push(`${g.suly_kg} kg`);
            if (g.rakmeter != null) bits.push(`${g.rakmeter} LDM`);
            if (g.kobmeter != null) bits.push(`${g.kobmeter} m³`);
            return (
              <div key={gi} style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text, #0d1220)", padding: "1px 4px" }}>
                · {bits.join(" · ") || "—"}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// γ-3-A — Read-only surcharge breakdown panel on the detail view. Groups by
// category (pre_informed / hidden / added_value), shows per-line totals and
// an aggregate. Mirrors the editor tints.
const SURCH_CAT_LABEL = {
  pre_informed: "Pre-informed (variable but expected)",
  hidden:       "Hidden / claim-sensitive",
  added_value:  "Added value (optional extras)",
};
const SURCH_CAT_TINT = {
  pre_informed: "#185FA5",
  hidden:       "#b22a29",
  added_value:  "#0f8a4a",
};
const SurchargesDetail = ({ surcharges = [], currency = "EUR" }) => {
  const groups = ["pre_informed", "hidden", "added_value"].map((cat) => ({
    cat,
    lines: (surcharges || []).filter((s) => s.category === cat),
  })).filter((g) => g.lines.length > 0);
  if (groups.length === 0) return null;
  const total = (surcharges || []).reduce((acc, ln) => {
    const t = ln.total != null ? Number(ln.total) : (ln.qty != null && ln.unit_cost != null ? Number(ln.qty) * Number(ln.unit_cost) : 0);
    return acc + (Number.isFinite(t) ? t : 0);
  }, 0);
  return (
    <Panel title="Surcharges breakdown">
      <div data-testid="logistics-detail-surcharges">
        {groups.map(({ cat, lines }) => (
          <div key={cat} className="mb-2" data-testid={`logistics-detail-surcharges-${cat}`}>
            <div className="flex items-center gap-2 mb-1">
              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 9999, background: SURCH_CAT_TINT[cat] }} aria-hidden="true" />
              <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 10, color: SURCH_CAT_TINT[cat], letterSpacing: "0.5px", textTransform: "uppercase" }}>{SURCH_CAT_LABEL[cat]}</span>
            </div>
            {lines.map((ln, i) => {
              const t = ln.total != null ? Number(ln.total) : (ln.qty != null && ln.unit_cost != null ? Number(ln.qty) * Number(ln.unit_cost) : null);
              return (
                <div key={i} className="flex items-center justify-between" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, padding: "2px 6px", borderBottom: "1px dashed rgba(0,0,0,0.06)" }}>
                  <span>
                    {ln.name || "—"}
                    {(ln.qty != null || ln.unit_cost != null || ln.unit) && (
                      <span style={{ color: "#6b7388" }}> {ln.qty ?? "—"}{ln.unit ? ` ${ln.unit}` : ""} {ln.unit_cost != null ? `× ${ln.unit_cost}` : ""}</span>
                    )}
                  </span>
                  <span style={{ fontWeight: 700 }}>{t != null ? t.toFixed(2) : "—"} {currency}</span>
                </div>
              );
            })}
          </div>
        ))}
        <div className="flex items-center justify-between pt-2 mt-2" style={{ borderTop: "1px solid var(--border-strong, rgba(0,0,0,0.10))" }}>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "var(--text-dimmer, #6b7388)", letterSpacing: "1px", textTransform: "uppercase" }}>Total surcharges</span>
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "#185FA5" }} data-testid="logistics-detail-surcharges-total">
            {total.toFixed(2)} {currency}
          </span>
        </div>
      </div>
    </Panel>
  );
};


// γ-3-C — Attachments panel: list + upload + download + delete. Tender
// pricing slot is highlighted; other attachments grouped by kind.
const ATTACHMENT_KIND_LABEL = {
  tender_pricing: "Tender pricing (Excel árazó)",
  customer_doc:   "Customer documents",
  internal:       "Internal documents",
  other:          "Other",
};
const ATTACHMENT_KIND_TINT = {
  tender_pricing: "#7e3aa8",
  customer_doc:   "#185FA5",
  internal:       "#6b7388",
  other:          "#9198b0",
};
const _fmtBytes = (n) => {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};
const AttachmentsPanel = ({ rid, attachments }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  // Local additions queued since the last LR refetch. Render = prop ⊕ local
  // mutations. Avoids the "setState-in-effect" anti-pattern while keeping
  // optimistic UI feel for upload/delete.
  const [added, setAdded] = useState([]);
  const [removedIds, setRemovedIds] = useState(new Set());
  const items = [
    ...(attachments || []).filter((a) => !removedIds.has(a.id)),
    ...added.filter((a) => !removedIds.has(a.id)),
  ];

  const handleUpload = async (e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // allow re-uploading the same file
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const att = await uploadAttachment(rid, file, kind);
      setAdded((arr) => [...arr, att]);
    } catch (ex) {
      setErr(String(ex?.message || ex));
    } finally {
      setBusy(false);
    }
  };
  const handleDelete = async (aid) => {
    if (!window.confirm("Delete this attachment?")) return;
    setBusy(true); setErr("");
    try {
      await deleteAttachment(rid, aid);
      setRemovedIds((s) => { const n = new Set(s); n.add(aid); return n; });
      setRefreshKey((k) => k + 1);
    } catch (ex) {
      setErr(String(ex?.message || ex));
    } finally {
      setBusy(false);
    }
  };

  const groups = ["tender_pricing", "customer_doc", "internal", "other"].map((k) => ({
    kind: k,
    items: items.filter((a) => a.kind === k),
  }));

  return (
    <Panel title="Attachments">
      <div data-testid="logistics-detail-attachments">
        {err && (
          <div className="mb-2 px-2 py-1 rounded" style={{ background: "rgba(178,42,41,0.10)", color: "#b22a29", fontFamily: "'DM Mono', monospace", fontSize: 11 }} data-testid="logistics-detail-attachments-error">
            {err}
          </div>
        )}
        {groups.map(({ kind, items: groupItems }) => {
          const tint = ATTACHMENT_KIND_TINT[kind];
          return (
            <div key={kind} className="mb-2" data-testid={`logistics-detail-attachments-group-${kind}`}>
              <div className="flex items-center gap-2 mb-1">
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 9999, background: tint }} aria-hidden="true" />
                <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 10, color: tint, letterSpacing: "0.5px", textTransform: "uppercase" }}>{ATTACHMENT_KIND_LABEL[kind]}</span>
                <label
                  className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded cursor-pointer"
                  style={{ background: tint, color: "#ffffff", fontFamily: "'DM Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: "0.4px", opacity: busy ? 0.6 : 1, pointerEvents: busy ? "none" : "auto" }}
                  data-testid={`logistics-detail-attachments-upload-${kind}`}
                >
                  <Upload size={11} /> {busy ? "Uploading…" : "Upload"}
                  <input type="file" className="hidden" onChange={(e) => handleUpload(e, kind)} disabled={busy} />
                </label>
              </div>
              {groupItems.length === 0 ? (
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#6b7388", padding: "2px 6px" }}>no files</div>
              ) : (
                groupItems.map((a) => (
                  <div key={a.id} className="flex items-center gap-2 py-1 px-2" style={{ borderBottom: "1px dashed rgba(0,0,0,0.06)" }} data-testid={`logistics-detail-attachment-${a.id}`}>
                    <Paperclip size={11} color="#6b7388" />
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.filename}</span>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#6b7388" }}>{_fmtBytes(a.size_bytes)}</span>
                    <a href={attachmentDownloadUrl(rid, a.id) + (refreshKey ? `?_=${refreshKey}` : "")} className="px-1.5 py-0.5 rounded" style={{ background: "transparent", border: "1px solid #185FA5", color: "#185FA5", fontFamily: "'DM Mono', monospace", fontSize: 9, fontWeight: 700, textDecoration: "none" }} data-testid={`logistics-detail-attachment-${a.id}-download`}>
                      <Download size={10} />
                    </a>
                    <button type="button" onClick={() => handleDelete(a.id)} className="px-1.5 py-0.5 rounded" style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.45)", color: "#b22a29", cursor: "pointer" }} data-testid={`logistics-detail-attachment-${a.id}-delete`}>
                      <Trash2 size={10} />
                    </button>
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>
    </Panel>
  );
};


// ─────────────────── iter95 — Additional parameters safety net ──────────────
//
// Lists any populated side-panel parameter that isn't already rendered above.
// Keeps the surfacing guarantee even if new fields are added to PARAMS in the
// editor without an explicit row on the detail page. Anything with a value
// will show up here automatically.
//
// γ-3-D refactor (iter95c) — the catalogue is imported from the shared
// `params` module so the editor + detail page can never drift.
import { PARAMS as _ALL_PARAMS, isFilled as _isFilled } from "./logistics/editor/params";
// Keys already shown above in a dedicated panel — kept in sync by hand. Anything
// MISSING here that's populated will appear in the safety-net panel.
const EXPLICITLY_RENDERED_KEYS = new Set([
  "azonosito", "ajanlat_jellege", "ajanlati_szint", "forgalom_iranya", "is_tender", "ajanlat_ervenyessege",
  "adr", "un_szam", "rakatolas_modja", "hutes_hofok", "kiegeszito_szolgaltatasok",
  "celar", "ajanlati_margin_pct", "elfogadott_dij",
  "fuvarozas_dij", "fuvarozas_koltseg", "fuvarozas_margin",
  "szallitmanyozas_dij", "szallitmanyozas_koltseg", "szallitmanyozas_margin",
  "added_value_dij", "added_value_koltseg", "added_value_margin",
  "konkurencia_dij", "amount",
  "felelos_user_id", "deal_tulajdonos_user_id", "ugyfelszolgalat_user_id",
  "arazo_user_id", "external_forwarder_name",
  "arazasra_kikuldve_at", "kezdesi_datum", "projekt_kezdete",
  "szerzodes_idotartama_months", "tender_atfutasi_napok", "kovetkezo_tender_date",
  "tender_feltoltes_at", "tender_kikuldes_at", "tender_visszaigazolas_filename",
  "bejovo_dokumentumok", "kikuldott_dokumentumok",
  "kiegeszito_info", "szakmai_kifogas", "closed_reason",
  // γ-3-G — Rendered by the dedicated Megvalósult panel above.
  "delay_minutes", "delay_notes", "executed_notes",
  // G.3 — Rendered by the dedicated <ContainerIntermodalPanel /> below.
  // Mode badges + container chip are in the header; the panel renders
  // ports, pre/on-carriage and connection points in full.
  "is_container_movement", "transport_modes",
  "port_of_loading", "port_of_discharge",
  "pre_carriage", "on_carriage",
  "intermodal_connection_points",
]);
const AdditionalParametersPanel = ({ r }) => {
  const missing = _ALL_PARAMS.filter((p) => !EXPLICITLY_RENDERED_KEYS.has(p.k) && _isFilled(r[p.k]));
  if (missing.length === 0) return null;
  return (
    <KvPanel
      title="Additional parameters"
      rows={missing.map((p) => [p.label, r[p.k]])}
    />
  );
};


// γ-3-E (iter95c) — Read-only completeness meter chip rendered next to the
// stage badge in the detail header. Same population logic as the editor's
// per-group meter — counts populated PARAMS values out of the catalogue total.
// Tinted by completeness band: <40% red · <70% amber · ≥70% green.
const CompletenessChip = ({ r }) => {
  const filled = _ALL_PARAMS.filter((p) => _isFilled(r[p.k])).length;
  const total = _ALL_PARAMS.length;
  const pct = total ? Math.round((filled / total) * 100) : 0;
  const tint = pct >= 70 ? "#0f8a4a" : pct >= 40 ? "#b7770d" : "#b22a29";
  return (
    <span
      data-testid="logistics-detail-completeness"
      title={`${filled} of ${total} side-panel parameters filled`}
      style={{
        display: "inline-block",
        padding: "1px 6px",
        marginLeft: 2,
        background: `${tint}1a`,
        color: tint,
        border: `1px solid ${tint}80`,
        borderRadius: 3,
        fontFamily: "'DM Mono', monospace",
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: "0.4px",
      }}
    >
      {filled}/{total} · {pct}%
    </span>
  );
};

